<template>
    <div class="menu-chips" :class="{ 'menu-chips--collapsed': collapsed }">
        <ul class="menu-chips__list" ref="scrollBarRef">
            <li class="menu-chips__chip"
                :class="{ 'selected': category.id == selectedCategory }"
                v-for="category in categories"
                :key="category.id"
                @click="scrollMenu(category.id)">
                {{ category.name }}
            </li>
        </ul>
        <button type="button"
                class="menu-chips__all"
                aria-label="all categories"
                @click="showAll = true">
            <IconArrowDown />
        </button>
        <MenuCategoryAll v-show="showAll"
                         @close="showAll = false"
                         :categories="categories"
                         :selected-category="selectedCategory"
                         @scrollMenu="scrollMenu"
                         :isShown="showAll" />
    </div>
</template>

<script>
import IconArrowDown from '@/components/icons/IconArrowDown.vue';
import MenuCategoryAll from './MenuCategoryAll.vue';

export default {
    name: "MenuCategorySelect",
    components: {
        IconArrowDown,
        MenuCategoryAll,
    },
    props: {
        categories: { type: Array, required: true },
        selectedCategory: { type: [String, Number], required: true },
        collapsed: { type: Boolean, default: false },
    },
    data() {
        return {
            showAll: false,
        };
    },
    watch: {
        selectedCategory(newVal) {
            if (newVal) {
                this.$nextTick(() => {
                    this.scrollSelect();
                });
            }
        }
    },
    methods: {
        scrollSelect() {
            const categoryElement = this.$refs.scrollBarRef.getElementsByClassName('selected')[0];
            if (!categoryElement) return;
            const categoryOffsetLeft = categoryElement.offsetLeft;
            const categoryWidth = categoryElement.offsetWidth;

            const centerPosition = categoryOffsetLeft - (this.$refs.scrollBarRef.clientWidth / 2) + (categoryWidth / 2);

            this.$refs.scrollBarRef.scrollTo({
                left: centerPosition,
                behavior: 'smooth',
            });
        },
        scrollMenu(id) {
            const el = document.getElementById(id);
            if (el) {
                el.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start',
                });
            }
        },
    }
};
</script>

<style lang="scss" scoped>
.menu-chips {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 2px 0 12px;
    user-select: none;

    transition: padding var(--dur-base) var(--ease-standard);
}

.menu-chips--collapsed {
    padding-bottom: 8px;
}

.menu-chips__list {
    display: flex;
    gap: 8px;
    list-style: none;

    /* overflow-x:auto forces overflow-y to clip too (per CSS), which chops the
       active chip's soft shadow at the list edge. Pad vertically to give the
       shadow room inside the clip box, then pull the box back with matching
       negative margins so layout spacing is unchanged. */
    margin: -8px 0 -20px;
    padding: 8px 4px 20px 20px;

    overflow-x: auto;
    scrollbar-width: none;

    &::-webkit-scrollbar { display: none; }
}

.menu-chips__chip {
    flex-shrink: 0;

    padding: 9px 15px;

    font-weight: var(--weight-bold);
    font-size: 13.5px;
    line-height: 1;
    white-space: nowrap;

    border: 1px solid var(--border-subtle);
    border-radius: var(--radius-pill);

    color: var(--text-secondary);
    background: var(--surface-card);
    cursor: pointer;

    transition: color var(--dur-fast) var(--ease-standard),
                background var(--dur-base) var(--ease-standard),
                box-shadow var(--dur-base) var(--ease-standard);

    &.selected {
        color: #fff;
        background: var(--grad-brand);
        border-color: transparent;
        box-shadow: 0 6px 14px var(--sm-glow, rgba(108, 27, 228, 0.28));
    }
}

.menu-chips__all {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    width: 38px;
    height: 38px;
    margin-right: 12px;

    border: 1px solid var(--border-subtle);
    border-radius: var(--radius-pill);
    background: var(--surface-card);
    color: var(--text-secondary);
    cursor: pointer;

    ::v-deep svg {
        width: 16px;
        height: 16px;
    }
}
</style>
